import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useMemo, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { compact } from 'lodash-es';
import { Button, Page, PageContentHeader, Spinner, TableList } from 'seagull';
import { calculateStartDate, calculateEndDate } from '~/components/TableFilters/DateRangeWithPresets/DateRangeWithPresets.utils';
import TableFilters from '~/components/TableFilters/TableFilters';
import { DateRange } from '~/components/TableFilters/types';
import { routes } from '~/constants';
import { useStore, useStoreActions, useIoContext } from '~/contexts';
import { useOnMount } from '~/hooks/useOnMount';
import { useSetDocumentTitle } from '~/hooks/useSetDocumentTitle';
import { useSortFromUrl } from '~/hooks/useSortFromUrl';
import { useLocaleKeys } from '~/i18n';
import { dedupeArray } from '~/infra/utils';
import { selectors } from '~/store/state-selectors';
import { formatNumericUSDate } from '~/utils';
import { ProposalReportEntryModel } from './ProposalReportEntryModel';
import styles from './ProposalsReport.module.scss';
import { makeCsv, makeFilterConfig, applyFrontendFilters, sortProposalsReportData, proposalsReportColumnDefs, proposalsReportHeaderItems, ProposalsReportSortColumnId } from './ProposalsReport.utils';
import { ProposalsReportRow } from './ProposalsReportRow';
import { filterLabels, FilterType, ProposalReportEntryStatus } from './types';
export const ProposalsReport = (param)=>{
    let { now } = param;
    const { t: texts } = useLocaleKeys('pages.reports.proposalsReport');
    const history = useHistory();
    const { api } = useStoreActions();
    const { downloadCsv, getNow } = useIoContext();
    const isVendor = useStore(selectors.user.isVendor);
    const businessId = useStore(selectors.user.businessId);
    const [proposalsReportData, setProposalsReportData] = useState([]);
    const [allProposalsReportData, setAllProposalsReportData] = useState([]);
    const [selectedFilters, setSelectedFilters] = useState({});
    const [isLoading, setIsLoading] = useState(false);
    const [isInitialSentDateFilter, setIsInitialSentDateFilter] = useState(true);
    const sortIds = compact(proposalsReportHeaderItems.map((item)=>item.sortId));
    const defaultSort = useMemo(()=>({
            colIndex: sortIds.indexOf(ProposalsReportSortColumnId.CompanyName),
            sort: 'asc'
        }), [
        sortIds
    ]);
    const { sort, changeSort } = useSortFromUrl({
        defaultSort,
        sortIds
    });
    const initialFilters = {
        [FilterType.SentDate]: {
            dateRange: [
                calculateStartDate(formatNumericUSDate(now), DateRange.Last12Months, now),
                calculateEndDate(formatNumericUSDate(now), DateRange.Last12Months, now)
            ],
            dateRangeOption: DateRange.Last12Months
        }
    };
    const fetchProposalsReport = async (filters, _sort)=>{
        if (!businessId) return;
        setIsLoading(true);
        try {
            const summaries = await api.relationship.getProposalsReportSummaries({
                filters,
                isVendor,
                businessId
            });
            const data = summaries.map((summary)=>ProposalReportEntryModel.fromSummary(summary));
            setProposalsReportData(data);
            const hasNoFilters = Object.keys(filters).length === 0;
            const hasOnlySentDateFilter = Object.keys(filters).length === 1 && filters[FilterType.SentDate];
            if (hasNoFilters || hasOnlySentDateFilter) setAllProposalsReportData(data);
        } finally{
            setIsLoading(false);
        }
    };
    useSetDocumentTitle('Proposals Report');
    useOnMount(()=>{
        setSelectedFilters(initialFilters);
        fetchProposalsReport(initialFilters, sort);
    });
    const filteredData = useMemo(()=>applyFrontendFilters(proposalsReportData, selectedFilters), [
        proposalsReportData,
        selectedFilters
    ]);
    const sortedData = useMemo(()=>sortProposalsReportData(filteredData, sort), [
        filteredData,
        sort
    ]);
    const filterConfig = useMemo(()=>makeFilterConfig({
            filtersOptions: {
                [FilterType.Status]: ProposalReportEntryStatus.NotReviewed,
                [FilterType.SentBy]: dedupeArray(allProposalsReportData.map((entry)=>entry.sentById())),
                [FilterType.CompanyName]: dedupeArray(allProposalsReportData.map((entry)=>entry.companyName())),
                [FilterType.AgreementName]: dedupeArray(allProposalsReportData.map((entry)=>entry.agreementName()))
            },
            now: getNow(),
            data: allProposalsReportData,
            isInitialSentDateFilter
        }), [
        allProposalsReportData,
        getNow,
        isInitialSentDateFilter
    ]);
    const onExport = ()=>{
        downloadCsv(makeCsv(sortedData), 'proposals.csv');
    };
    const onSortChange = (sort)=>{
        changeSort(sort);
        fetchProposalsReport(selectedFilters, sort);
    };
    return /*#__PURE__*/ _jsx(Page, {
        testId: "proposals-report",
        header: texts.title(),
        variant: "containedHeight",
        onClickBack: ()=>history.push(routes.reports),
        actions: /*#__PURE__*/ _jsx(Button, {
            testId: "proposals-report-export-btn",
            disabled: isLoading,
            onClick: onExport,
            children: "Export"
        }),
        children: /*#__PURE__*/ _jsxs(TableList.Provider, {
            columnDefs: proposalsReportColumnDefs,
            numRows: sortedData.length,
            sort: sort,
            onSortChange: onSortChange,
            children: [
                /*#__PURE__*/ _jsx(PageContentHeader, {
                    children: /*#__PURE__*/ _jsx("div", {
                        className: styles.pageContent,
                        children: /*#__PURE__*/ _jsx(TableFilters, {
                            testIdPrefix: "proposals-report",
                            filterLabels: filterLabels,
                            filterConfig: filterConfig,
                            selectedFilters: selectedFilters,
                            setSelectedFilters: (newFilters)=>{
                                if (newFilters[FilterType.SentDate] !== selectedFilters[FilterType.SentDate]) setIsInitialSentDateFilter(false);
                                setSelectedFilters(newFilters);
                            },
                            onRefresh: fetchProposalsReport
                        })
                    })
                }),
                /*#__PURE__*/ _jsx(TableList.Structure, {
                    header: /*#__PURE__*/ _jsx(TableList.Header, {
                        stickyToTop: true,
                        items: proposalsReportHeaderItems,
                        testId: "proposals-report-table-header"
                    }),
                    content: isLoading ? /*#__PURE__*/ _jsx(Spinner, {
                        centeredInBox: true
                    }) : /*#__PURE__*/ _jsx(_Fragment, {
                        children: sortedData.map((row, index)=>/*#__PURE__*/ _jsx(ProposalsReportRow, {
                                index: index,
                                data: row
                            }, index))
                    })
                })
            ]
        })
    });
};
